import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { clearCart, removeFromCart } from '../redux/slices/cartSlice';
import { PageBackButton } from '../components';
import { useCheckoutForm } from '../hooks/useCheckoutForm';
import { useOrderCalculations } from '../hooks/useOrderCalculations';
import { useOrder } from '../hooks/useOrder';
import { useAppNavigate } from '../hooks/useAppNavigate';
import { useDelivery } from '../hooks/useDelivery';
import {
  CheckoutForm,
  OrderSummary,
  EmptyCart,
  PayLaterSuccessPopup,
  PayLaterFailurePopup,
  DeliveryQuotePopup,
} from '../components/checkout';
import { ScheduleConflictPopup } from '../components/checkout/ScheduleConflictPopup';

const CheckoutPage: React.FC = () => {
  const appNavigate = useAppNavigate();
  const { items: cartItems } = useAppSelector(state => state.cart);
  const websiteConfig = useAppSelector(state => state.siteContent.websiteConfig);
  const dispatch = useAppDispatch();
  
  const [isConflictPopupOpen, setIsConflictPopupOpen] = useState(false);
  const [invalidScheduleItems, setInvalidScheduleItems] = useState<any[]>([]);

  const handleScheduleConflict = useCallback((invalidItems: any[]) => {
    setInvalidScheduleItems(invalidItems);
    setIsConflictPopupOpen(true);
  }, []);

  const handleScheduleResolved = useCallback(() => {
    setIsConflictPopupOpen(false);
    setInvalidScheduleItems([]);
  }, []);

  const {
    activeTab,
    setActiveTab,
    formData,
    errors,
    handleChange,
    validateForm,
  } = useCheckoutForm('pickup', {
    cartItems,
    onScheduleConflict: handleScheduleConflict,
    onScheduleResolved: handleScheduleResolved,
  });
  
  const { subtotal, deliveryFee, tax, total } = useOrderCalculations(cartItems, activeTab);
  
  const {
    handleOrder,
    orderLoading,
    paymentMode,
    showSuccessPopup,
    showErrorPopup,
    handleErrorClose,
    orderResponse,
  } = useOrder();

  const {
    fetchDeliveryQuote,
    isLoadingQuote,
    deliveryQuote,
    isQuotePopupOpen,
    setIsQuotePopupOpen,
  } = useDelivery();

  const { showPayNow, showPayLater, showDelivery } = useMemo(() => {
    return {
      showPayNow: websiteConfig?.metadata?.enabledPaynow ?? true,
      showPayLater: websiteConfig?.metadata?.enabledPaylater ?? true,
      showDelivery: websiteConfig?.metadata?.enabledDelivery ?? false,
    };
  }, [websiteConfig?.metadata]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleOrderSubmissionAttempt = useCallback((paymentType: 'pay_now' | 'pay_later') => {
    const isValid = validateForm();
    if (!isValid) return;

    const quoteId = activeTab === 'delivery' ? deliveryQuote?.id : undefined;
    const deliveryFeeValue = activeTab === 'delivery' && deliveryQuote?.fee 
      ? deliveryQuote.fee / 100  
      : undefined;

    const sanitizedFormData = {
      ...formData,
      specialRequests: formData.specialRequests || '',
      address_line_1: formData.address_line_1 || '',
      address_line_2: formData.address_line_2 || '',
      locality: formData.locality || '',
      administrative_district_level_1: formData.administrative_district_level_1 || '',
      postal_code: formData.postal_code || '',
    };

    handleOrder(sanitizedFormData, total, activeTab, paymentType, quoteId, deliveryFeeValue);
  }, [validateForm, activeTab, deliveryQuote, handleOrder, formData, total]);

  const handleFormSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    handleOrderSubmissionAttempt('pay_now');
  }, [handleOrderSubmissionAttempt]);

  const handleTakeoutSubmit = useCallback(() => {
    handleOrderSubmissionAttempt('pay_later');
  }, [handleOrderSubmissionAttempt]);

  const handleProceedWithoutInvalid = useCallback(() => {
    invalidScheduleItems.forEach((cartItem) => {
      const uniqueId = cartItem.cartItemId || cartItem.id || cartItem.item?.pk_id;
      if (uniqueId) {
        dispatch(removeFromCart(uniqueId));
      }
    });

    setIsConflictPopupOpen(false);
    setInvalidScheduleItems([]);
  }, [invalidScheduleItems, dispatch]);

  const handleCloseConflictPopup = useCallback(() => {
    setIsConflictPopupOpen(false);
  }, []);

  const handleDeliveryContinue = useCallback(async () => {
    if (validateForm()) {
      await fetchDeliveryQuote(formData);
    }
  }, [validateForm, fetchDeliveryQuote, formData]);

  const handlePlaceDeliveryOrder = useCallback(() => {
    const quoteId = deliveryQuote?.id;
    const deliveryFeeValue = deliveryQuote?.fee ? deliveryQuote.fee / 100 : undefined;
    const sanitizedFormData = { ...formData, specialRequests: formData.specialRequests || '' };

    handleOrder(sanitizedFormData, total, 'delivery', 'pay_now', quoteId, deliveryFeeValue);
  }, [deliveryQuote, handleOrder, formData, total]);

  useEffect(() => {
    if (orderResponse && isQuotePopupOpen) {
      setIsQuotePopupOpen(false);
    }
  }, [orderResponse, isQuotePopupOpen, setIsQuotePopupOpen]);

  if (cartItems.length === 0) {
    return <EmptyCart />;
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 flex flex-col">
      <div className="container mx-auto py-12 px-4">
        <PageBackButton text="Menu" onClick={() => appNavigate('/menu')} />
        <div className="mb-8">
          <h1 className="text-4xl font-bold">Checkout</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <CheckoutForm
              formData={formData}
              errors={errors}
              handleChange={handleChange}
              onSubmit={handleFormSubmit}
              onTakeoutOrder={handleTakeoutSubmit}
              onContinue={handleDeliveryContinue}
              orderLoading={orderLoading}
              paymentMode={paymentMode}
              quoteLoading={isLoadingQuote}
              cartItemsCount={cartItems.length}
              showPayNow={showPayNow}
              showPayLater={showPayLater}
              showDelivery={showDelivery}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
          </div>

          <div>
            <OrderSummary
              cartItems={cartItems}
              orderType={activeTab}
              subtotal={subtotal}
              deliveryFee={deliveryFee}
              tax={tax}
              total={total}
            />
          </div>
        </div>
      </div>

      <ScheduleConflictPopup
        isOpen={isConflictPopupOpen}
        invalidItems={invalidScheduleItems}
        onClose={handleCloseConflictPopup}
        onProceedWithoutInvalid={handleProceedWithoutInvalid}
      />

      <PayLaterSuccessPopup
        isOpen={showSuccessPopup}
        orderNumber={orderResponse?.orderNumber}
        scheduledFor={orderResponse?.pickupDetails?.scheduledFor}
        fulfillmentType={orderResponse?.fulfillmentType}
        onContinue={() => {
          dispatch(clearCart());
          appNavigate('/menu');
        }}
      />

      <PayLaterFailurePopup
        isOpen={showErrorPopup}
        onClose={handleErrorClose}
        onTryAgain={handleErrorClose}
      />

      <DeliveryQuotePopup
        show={isQuotePopupOpen}
        onClose={() => setIsQuotePopupOpen(false)}
        quoteData={deliveryQuote}
        cartItems={cartItems}
        subtotal={subtotal}
        onPlaceOrder={handlePlaceDeliveryOrder}
        orderLoading={orderLoading}
      />
    </div>
  );
};

export default CheckoutPage;